import { useState, useEffect } from 'react';

const BANNER_DISMISSED_KEY = 'urgency_banner_dismissed';

// Bereken de eerstvolgende zondag
const getNextSunday = (): string => {
  const now = new Date();
  const day = now.getDay(); // 0 = zondag
  const daysUntilSunday = day === 0 ? 7 : 7 - day;
  const sunday = new Date(now);
  sunday.setDate(now.getDate() + daysUntilSunday);
  return sunday.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });
};

export const UrgencyBanner = () => {
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissed) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;

    const calcTimeLeft = () => {
      const now = new Date();
      const day = now.getDay();
      const daysUntilSunday = day === 0 ? 0 : 7 - day;
      const sunday = new Date(now);
      sunday.setDate(now.getDate() + daysUntilSunday);
      sunday.setHours(23, 59, 59, 0);
      const diff = sunday.getTime() - now.getTime();
      if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0 };
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      return { hours, minutes, seconds };
    };

    setTimeLeft(calcTimeLeft());
    const interval = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, [visible]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  };

  if (!visible) return null;

  const pad = (n: number) => String(n).padStart(2, '0');
  const sunday = getNextSunday();

  return (
    <div className="relative z-[60] bg-gradient-to-r from-violet-700 via-fuchsia-600 to-violet-700 text-white">
      <div className="flex items-center justify-center gap-3 md:gap-6 px-10 py-2.5 text-center flex-wrap">
        {/* Icon + tekst */}
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-truck-line text-base" />
          </div>
          <span className="text-sm font-semibold tracking-wide whitespace-nowrap">
            Gratis verzending t/m zondag {sunday}
          </span>
        </div>

        {/* Separator */}
        <span className="hidden md:block text-white/40">|</span>

        {/* Countdown */}
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-time-line text-sm text-white/80" />
          </div>
          <span className="text-xs text-white/80 whitespace-nowrap">Nog:</span>
          <div className="flex items-center gap-1">
            <span className="bg-white/20 rounded px-1.5 py-0.5 text-xs font-mono font-bold tabular-nums">
              {pad(timeLeft.hours)}u
            </span>
            <span className="text-white/60 text-xs">:</span>
            <span className="bg-white/20 rounded px-1.5 py-0.5 text-xs font-mono font-bold tabular-nums">
              {pad(timeLeft.minutes)}m
            </span>
            <span className="text-white/60 text-xs">:</span>
            <span className="bg-white/20 rounded px-1.5 py-0.5 text-xs font-mono font-bold tabular-nums">
              {pad(timeLeft.seconds)}s
            </span>
          </div>
        </div>

        {/* Separator */}
        <span className="hidden md:block text-white/40">|</span>

        {/* CTA */}
        <a
          href="/shop"
          className="text-xs font-bold underline underline-offset-2 whitespace-nowrap hover:text-white/80 transition-colors cursor-pointer"
        >
          Shop nu &rarr;
        </a>
      </div>

      {/* Sluit knop */}
      <button
        onClick={handleDismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
        aria-label="Banner sluiten"
      >
        <i className="ri-close-line text-base" />
      </button>
    </div>
  );
};
